import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd, RouterEvent } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
	LoginService,
	CheckboxComponent,
	NativeStateComponent,
	TableColumn,
	IdentifierTableColumn,
	DateTableColumn,
	ButtonTableColumn,
	TableComponent,
	DataReloadEvent,
	TableElement
} from '@serengeti/serengeti-common';
import { CatalogInstanceService } from '@serengeti/serengeti-app';
import { NotificationService, NotificationSubject } from '@serengeti/serengeti-notification';

@Component({
  selector: 'app-service-catalog-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss']
})
export class ServiceCatalogListComponent implements OnInit, OnDestroy {

	searchName: string;
	oldName: string;

	currentCondition: DataReloadEvent;
	columns: TableColumn[];
	detailColumn: TableColumn;

	@ViewChild('serviceList', {static: true}) serviceList: TableComponent;

	routeSubscription: Subscription;
	notificationSubscription: Subscription;

	constructor(
		private catalogInstanceService: CatalogInstanceService,
		private router: Router,
		private route: ActivatedRoute,
		private notificationService: NotificationService,
		private loginService: LoginService,
		private translateService: TranslateService
	) {
		this.columns = [
			new IdentifierTableColumn('id', 'software.catalog.instance.list.content.columns.select')
			.withValueVisible(false)
			.withType(CheckboxComponent),
			new TableColumn('owner.name', 'software.catalog.instance.list.content.columns.owner.name'),
			new TableColumn('tenant.name', 'software.catalog.instance.list.content.columns.tenant.name')
			.withDecorator((_column: TableColumn, _element: TableElement) => {
				const catalogInstance = _element.getData();

				if (catalogInstance.tenant) {
					if (catalogInstance.tenant.name === 'default') {
						return 'software.catalog.instance.list.content.tenant.default'
					} else {
						return catalogInstance.tenant.name;
					}
				} else {
					return 'software.catalog.instance.list.content.tenant.unknown';
				}
			}),
			new TableColumn('name', 'software.catalog.instance.list.content.columns.name'),
			new TableColumn('version', 'software.catalog.instance.list.content.columns.version')
			.withIgnoreSort(true)
			.withStyles('cellCenter'),
			new TableColumn('category.name', 'software.catalog.instance.list.content.columns.category')
			.withIgnoreSort(true),
			new TableColumn('status', 'software.catalog.instance.list.content.columns.state')
			.withType(NativeStateComponent)
			.withStyles('cellCenter')
			.withDecorator((_column: TableColumn, _element: TableElement) => {
				const state = TableColumn._getData(_element.getData(), 'status');
				if (state === 'Running') {
					_element.getData()._nativeState = 'status running';
					return 'resource.instance.common.state.running';
				} else if (state === 'Pending') {
					_element.getData()._nativeState = 'status pending';
					return 'resource.instance.common.state.pending';
				} else if (state === 'Starting') {
					_element.getData()._nativeState = 'status starting';
					return 'resource.instance.common.state.starting';
				} else if (state === 'Failed') {
					_element.getData()._nativeState = 'status failed';
					return 'resource.instance.common.state.failed';
				} else if (state === 'Error') {
					_element.getData()._nativeState = 'status error';
					return 'resource.instance.common.state.error';
				} else if (state === 'Terminated') {
					_element.getData()._nativeState = 'status terminated';
					return 'resource.instance.common.state.terminated';
				} else {
					_element.getData()._nativeState = 'status stopped';
					return 'resource.instance.common.state.stopped';
				}
			}),
			new DateTableColumn('createdDate', 'software.catalog.instance.list.content.columns.created-date')
			.withStyles('cellCenter'),
			new DateTableColumn('updatedDate', 'software.catalog.instance.list.content.columns.updated-date')
			.withStyles('cellCenter'),
			this.detailColumn = new ButtonTableColumn('software.catalog.instance.list.content.columns.detail',
			'software.catalog.instance.list.content.columns.detail', null, null, 'dehaze')
			.withIgnoreSort(true)
			.withStyles('cellCenter')
		];

		this.routeSubscription = this.router.events.pipe(filter((event: RouterEvent) => event instanceof NavigationEnd))
		.subscribe(() => {
			this.doSearch();

			this.notificationSubscription = this.notificationService
			.listenAll("ServiceCatalogList",
			[
				NotificationSubject.ResourceState,
				NotificationSubject.ResourceCatalogState,
				NotificationSubject.SoftwareCatalogState
			] as string[]).subscribe((message: any) => {
				try {
					if (NotificationSubject.resourceTypes.includes(message.message.action.resourceType)
					|| NotificationSubject.resourceCatalogType.includes(message.message.action.resourceType)
					|| NotificationSubject.softwareCatalogType.includes(message.message.action.resourceType)) {
						this.doRefresh();
					}
				} catch (ex) {
					console.log(ex);
				}
			});
		});

	}

	ngOnInit() {
	}

	ngOnDestroy() {
		if (this.routeSubscription) {
			this.routeSubscription.unsubscribe();
		}
		if (this.notificationSubscription) {
			this.notificationSubscription.unsubscribe();
		}
	}

	doSearch() {
		if (!this.oldName || this.oldName !== this.searchName) {
			this.oldName = this.searchName;

			this.doRefresh();
		}
	}

	doSearchInternal() {
		this.doRefresh();
	}

	doRefresh(condition?: DataReloadEvent) {
		if (!condition) {
			const sortItem = this.loginService.getScreenConfig('service.catalog.list', 'sortItem');
			const sortOrder = this.loginService.getScreenConfig('service.catalog.list', 'sortOrder');

			condition = new DataReloadEvent((sortItem ? sortItem : 'name'), (sortOrder ? sortOrder : 'desc'), 0, 15, this.searchName);
		}

		if (condition) {
			this.currentCondition = condition;
		} else {
			condition = this.currentCondition;
		}
		if (condition.pageNumber === 0) {
			this.loginService.setScreenConfig('service.catalog.list', 'sortItem', condition.active);
			this.loginService.setScreenConfig('service.catalog.list', 'sortOrder', condition.sortOrder);
		}

    this.catalogInstanceService.search(null, null, null, condition.filter,
      condition.active, condition.sortOrder, condition.pageNumber, condition.pageSize)
      .subscribe((resultPage: any) => {
      this.serviceList.refresh(resultPage.totalElements, resultPage.content, this.columns, condition);
    });
	}

	onDetailButtonClicked($event: any) {
		if ($event.column === this.detailColumn) {
			this.router.navigate(['/main', { outlets: { content: 'software/catalog/instance/detail/' + $event.element.getData().id + '/' }}]);
		}
	}

	doLaunch() {
		this.router.navigate(['/main', { outlets: { content: 'service/catalog/launcher' }}]);
	}

	doTerminate() {
		this.translateService.get([
			'software.catalog.instance.list.alert.invalid-terminating-instances',
			'software.catalog.instance.list.alert.terminating-instances-started',
			'software.catalog.instance.list.confirm.terminate-instances'
		]).subscribe((messages: any) => {
			let invalidInstances = false;
			const selectedInstances = [];
			this.serviceList.getElements().forEach((element: TableElement) => {
				if (element.isSelected()) {
					const instance = element.getData();

					if (instance.status === 'Terminated') {
						invalidInstances = true;
					} else {
						selectedInstances.push(element.getData());
					}
				}
			});

			if (invalidInstances) {
				alert(messages['software.catalog.instance.list.alert.invalid-terminating-instances']);
			} else {
				if (confirm(messages['software.catalog.instance.list.confirm.terminate-instances'])) {
					this.catalogInstanceService.terminateAll(selectedInstances.map((catalogInstance: any) => catalogInstance.id) as string[]).subscribe(() => {});
					alert(messages['software.catalog.instance.list.alert.terminating-instances-started']);
				}
			}
		});
	}

	doDelete() {
		this.translateService.get([
			'software.catalog.instance.list.alert.invalid-deleting-instances',
			'software.catalog.instance.list.alert.deleting-instances-started',
			'software.catalog.instance.list.confirm.delete-instances'
		]).subscribe((messages: any) => {
			let invalidInstances = false;
			const selectedInstances = [];
			this.serviceList.getElements().forEach((element: TableElement) => {
				if (element.isSelected()) {
					const instance = element.getData();

					if (instance.status === 'Terminated') {
						selectedInstances.push(element.getData());
					} else {
						invalidInstances = true;
					}
				}
			});

			if (invalidInstances) {
				alert(messages['software.catalog.instance.list.alert.invalid-deleting-instances']);
			} else {
				if (confirm(messages['software.catalog.instance.list.confirm.delete-instances'])) {
					alert(messages['software.catalog.instance.list.alert.deleting-instances-started']);
					this.catalogInstanceService.deleteAll(selectedInstances.map(catalogInstance => catalogInstance.id)).subscribe(() => {
						this.doRefresh();
					});
				}
			}
		});
	}

}
